import { IconButton } from "@fluentui/react";
import { getNextColor, IChartProps } from "@fluentui/react-charting";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { LineChartComponent } from "../../../../ChartComponents/LineChartFluent";
import { breakpoints } from "../../../../helper/ScreenBreakPoints";
import CustomCard from "../../../../SharedComponents/CustomCard";
import { iconButtonStyles } from "../../../../SharedComponents/Styles/CustomCardWithMetrics.styles";
import { selectViewport } from "../../../../slices/UI.slice";
import { useAppSelector } from "../../../../store/hooks";
import { getCustomerFeatureChartStyles } from "../../../CustomerView/Features/CustomerFeatureChart.styles";
import { PRODUCT_VIEW_STYLES } from "../../Common/ProductView.styles";

export const ProductFeatureChart: React.FC<any> = ({
  carouseldata,
  width,
  granularity = "Monthly",
  isLoading,
  kustoLink
}) => {
  const [cardWidth, setCardWidth] = useState<number>(300);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const resizedWidth = useAppSelector((state) => selectViewport(state).offsetWidth);
  const [, setOffsetWidth] = useState(resizedWidth);
  const containerRef = useRef<HTMLDivElement>(null);

  const calculateWidth = () => {
    if (!containerRef.current) return;

    let itemsPerRow = 1;
    for (const bp of breakpoints) {
      if (window.innerWidth >= bp.width) {
        itemsPerRow = bp.items;
        break;
      }
    }

    const availableWidth = containerRef.current.offsetWidth;
    const newCardWidth = Math.floor(availableWidth / itemsPerRow) - 16;
    setCardWidth(newCardWidth);
  };

  useLayoutEffect(() => {
    if (containerRef.current) {
      calculateWidth();
    }
  });

  useEffect(() => {
    setOffsetWidth(resizedWidth);
  }, [resizedWidth]);

  useEffect(() => {
    setCurrentDate(new Date()); // Reset to today when granularity changes
  }, [granularity]);

  const styles = getCustomerFeatureChartStyles(cardWidth);

  const GetItemData = (item: any, idx: number) => {
    // Transform raw data to chart format
    const featureGroups: Record<string, any[]> = item.data?.reduce((acc: any, curr: any) => {
      if (!acc[curr.featureName]) acc[curr.featureName] = [];
      acc[curr.featureName].push(curr);
      return acc;
    }, {});

    const chartLines = featureGroups
      ? Object.entries(featureGroups).map(([featureName, dataArr]: [string, any[]], idx: number) => ({
          legend: featureName,
          data: dataArr.map((d: any) => ({
            x: new Date(d.snapshotDate),
            y: Number(d.currentValue)
          })),
          color: getNextColor(idx),
          lineOptions: {
            lineBorderWidth: "4"
          }
        }))
      : [];

    // For daily granularity, implement windowing with navigation
    if (granularity === "Daily") {
      const allDataPoints = chartLines
        .flatMap((line: any) => line.data)
        .sort((a: any, b: any) => a.x.getTime() - b.x.getTime());

      if (allDataPoints.length === 0) {
        return (
          <div key={idx} style={{ width: "100%" }}>
            <CustomCard
              cardHeaderTitle={item.name}
              styles={PRODUCT_VIEW_STYLES.TAB.FEATURE_VIEW_SUMMARY_TAB.CSS_STYLES.customCardStyles}
              kustoLink={kustoLink}
            >
              <div style={{ padding: 16, textAlign: "center" }}>No data available</div>
            </CustomCard>
          </div>
        );
      }

      // Calculate 30-day window around currentDate
      const getValidDataWindow = (centerDate: Date) => {
        const maxDate = allDataPoints[allDataPoints.length - 1].x; // Use the latest available data point

        // If centerDate is after maxDate, adjust it to maxDate
        const adjustedCenterDate = centerDate > maxDate ? maxDate : centerDate;

        const attemptDate = new Date(
          adjustedCenterDate.getFullYear(),
          adjustedCenterDate.getMonth(),
          adjustedCenterDate.getDate()
        );

        for (let i = 0; i < 12; i++) {
          const windowEnd = new Date(attemptDate);
          const windowStart = new Date(attemptDate);
          windowStart.setDate(windowEnd.getDate() - 30);

          const linesWithData = chartLines.map((line: any) => ({
            ...line,
            data: line.data.filter((point: any) => point.x >= windowStart && point.x <= windowEnd)
          }));

          const hasData = linesWithData.some((line: any) => line.data.length > 0);
          if (hasData) {
            return { visibleLines: linesWithData, windowStart, windowEnd };
          }

          attemptDate.setDate(attemptDate.getDate() - 30);
        }

        return {
          visibleLines: chartLines.map((line: any) => ({ ...line, data: [] })),
          windowStart: new Date(adjustedCenterDate),
          windowEnd: new Date(adjustedCenterDate)
        };
      };

      const { visibleLines, windowStart, windowEnd } = getValidDataWindow(currentDate);

      const maxDate = allDataPoints[allDataPoints.length - 1].x;
      const hasNext = allDataPoints.some((p: any) => p.x > windowEnd) && windowEnd < maxDate;
      const hasPrevious = allDataPoints.some((p: any) => p.x < windowStart);

      // Generate tick values for X-axis
      const getAllTickValues = () => {
        return Array.from(new Set(visibleLines.flatMap((line: any) => line.data.map((p: any) => p.x.getTime())))).map(
          (timestamp: any) => new Date(timestamp)
        );
      };

      const tickValues = getAllTickValues();
      const chartData: IChartProps = { lineChartData: visibleLines };

      return (
        <div key={idx} style={{ width: "100%", padding: 8 }}>
          <CustomCard
            cardHeaderTitle={item.name}
            styles={PRODUCT_VIEW_STYLES.TAB.FEATURE_VIEW_SUMMARY_TAB.CSS_STYLES.customCardStyles}
            kustoLink={kustoLink}
          >
            <LineChartComponent
              data={chartData}
              isLoading={isLoading}
              height={350}
              granularity={granularity}
              tickValues={tickValues}
              allowMultipleShapesForPoints
              allowResize
            />

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                position: "relative",
                zIndex: 999,
                marginTop: 12,
                top: -5,
                gap: 10
              }}
            >
              <IconButton
                iconProps={{ iconName: "ChevronLeft" }}
                title="Previous"
                onClick={() =>
                  setCurrentDate((prev) => {
                    const newDate = new Date(prev);
                    newDate.setDate(newDate.getDate() - 30);
                    return newDate;
                  })
                }
                disabled={!hasPrevious}
                styles={iconButtonStyles}
              />
              <IconButton
                iconProps={{ iconName: "ChevronRight" }}
                title="Next"
                onClick={() =>
                  setCurrentDate((prev) => {
                    const newDate = new Date(prev);
                    newDate.setDate(newDate.getDate() + 30);
                    // Don't go beyond the latest available data point
                    return newDate > maxDate ? maxDate : newDate;
                  })
                }
                disabled={!hasNext}
                styles={iconButtonStyles}
              />
            </div>
          </CustomCard>
        </div>
      );
    } else {
      // For monthly and other granularities, show only month-end data for monthly
      let filteredChartLines = chartLines;

      if (granularity === "Monthly") {
        filteredChartLines = chartLines.map((line: any) => {
          // Group data points by year and month, then pick the last (max date) for each group
          const monthMap: Record<string, any> = {};
          line.data.forEach((point: any) => {
            const key = `${point.x.getFullYear()}-${point.x.getMonth()}`;
            if (!monthMap[key] || point.x > monthMap[key].x) {
              monthMap[key] = point;
            }
          });
          return {
            ...line,
            data: Object.values(monthMap).sort((a: any, b: any) => a.x - b.x)
          };
        });
      }

      const chartData: IChartProps = {
        lineChartData: filteredChartLines
      };

      return (
        <div key={idx} style={{ width: "50%", padding: 10 }}>
          <CustomCard
            cardHeaderTitle={item.name}
            styles={PRODUCT_VIEW_STYLES.TAB.FEATURE_VIEW_SUMMARY_TAB.CSS_STYLES.customCardStyles}
            kustoLink={kustoLink}
          >
            <LineChartComponent
              data={chartData}
              isLoading={isLoading}
              height={400}
              width={width}
              granularity={granularity}
              allowMultipleShapesForPoints
              allowResize
            />
          </CustomCard>
        </div>
      );
    }
  };

  return (
    <div ref={containerRef} style={styles.container}>
      {carouseldata?.items?.length > 0 ? (
        carouseldata.items.map((m: any, idx: number) => GetItemData(m, idx))
      ) : (
        <span>No data available</span>
      )}
    </div>
  );
};
